import * as THREE from 'three'
import { CameraControls } from './CameraControls'
import {
  DEFAULT_ZONE_REACTION,
  type AudioFrame,
  type CameraPose,
  type PrimitiveKind,
  type SceneDescription,
  type TransitionConfig,
  type VisualParams,
  type ZoneReactionConfig,
} from '../types'
import {
  createFreqTexture,
  createWaveTexture,
  createDummyTexture,
  createSharedUniforms,
  updateUniforms,
  type SharedUniforms,
} from './AudioUniforms'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'
import { CameraDirector } from './CameraDirector'
import { TransitionCompositor } from './TransitionCompositor'
import type { Primitive } from './primitives/Primitive'
import { PointCloud } from './primitives/PointCloud'
import { LineField } from './primitives/LineField'
import { TriangleField } from './primitives/TriangleField'
import { TerrainField } from './primitives/TerrainField'
import { Waveform } from './primitives/Waveform'
import { Constellation } from './primitives/Constellation'
import { Tunnel } from './primitives/Tunnel'
import { Vortex } from './primitives/Vortex'
import { LedPanel } from './primitives/LedPanel'
import { Water } from './primitives/Water'
import { Fireworks } from './primitives/Fireworks'
import { ProjectileRain } from './primitives/ProjectileRain'
import { Smoke } from './primitives/Smoke'
import { Plasma } from './primitives/Plasma'
import { Metaballs } from './primitives/Metaballs'
import { Spectrum } from './primitives/Spectrum'
import { Galaxy } from './primitives/Galaxy'
import { Aurora } from './primitives/Aurora'
import { Ferro } from './primitives/Ferro'
import { VideoDisplaceField } from './primitives/VideoDisplaceField'
import { VideoLabRecolor, type VideoLabAppearance } from './VideoLabRecolor'
import { ZoneReaction } from './ZoneReaction'
import { InsideLook } from './InsideLook'
import { buildPaletteLUT } from './palette'

// The 3D scene is a stateful singleton (renderer, camera, primitive); hot-reload
// does not recreate it. Force a FULL page reload when this module changes so we
// never keep running a stale engine.
// (Vite HMR hook in the original project; harmless no-op under CRA where
// import.meta.hot is undefined.)
const _viteHot = (import.meta as unknown as { hot?: { accept: (cb: () => void) => void } }).hot;
if (_viteHot) {
  _viteHot.accept(() => window.location.reload());
}

// Live debug snapshot, updated each frame by the running scene and read by the
// on-screen debug console (toggled from the UI). A plain key→value record so we
// can drop in whatever we need to inspect without touching the console UI.
let sceneDebug: Record<string, number | string> = {}
export function getSceneDebug(): Record<string, number | string> {
  return sceneDebug
}
// Live smoothed audio energy (0..1), exposed for the Director's mood matching.
let audioEnergy = 0
export function getAudioEnergy(): number {
  return audioEnergy
}
const r2 = (n: number) => Math.round(n * 100) / 100
const r3 = (n: number) => Math.round(n * 1000) / 1000

type AudioReader = (dt: number) => AudioFrame

// Shared GLSL: maps a 0..1 UV through a mirror/kaleidoscope effect.
//   uMirror: 0 none, 1 horizontal, 2 vertical, 3 quad, 4 kaleidoscope.
//   uMirrorAmount: density (number of folds / segments).
// triWave is a continuous triangle wave (period 2, range 0..1) so reflections
// stitch seamlessly at every fold. The mirror is applied in SCREEN space and the
// texture is sampled with MirroredRepeat wrap, so the result always closes — no
// hard division at the seams, even when the folded coordinate leaves 0..1.
const MIRROR_GLSL = /* glsl */ `
  uniform float uMirror;
  uniform float uMirrorAmount;
  float triWave(float x) { return abs(fract(x * 0.5) * 2.0 - 1.0); }
  // aspect = width/height of the space, used to keep the kaleidoscope circular.
  vec2 applyMirror(vec2 uv, float aspect) {
    int t = int(uMirror + 0.5);
    float a = max(1.0, uMirrorAmount);
    if (t == 1) return vec2(triWave(uv.x * a), uv.y);
    if (t == 2) return vec2(uv.x, triWave(uv.y * a));
    if (t == 3) return vec2(triWave(uv.x * a), triWave(uv.y * a));
    if (t == 4) {
      vec2 c = uv - 0.5;
      c.x *= aspect;
      float r = length(c);
      float ang = atan(c.y, c.x);
      float seg = 6.28318530718 / a;
      ang = mod(ang, seg);
      ang = abs(ang - 0.5 * seg);
      c = vec2(cos(ang), sin(ang)) * r;
      c.x /= aspect;
      return c + 0.5;
    }
    return uv;
  }
`

// Velocity profile: a bell (0→1→0) over u in [0,1]; ease (0..1) sharpens the peak.
function vProfile(u: number, ease: number): number {
  const bell = Math.sin(Math.PI * Math.max(0, Math.min(1, u)))
  return Math.pow(bell, 0.5 + ease * 2)
}

// cubic-bezier(easeIn, 0, 1 - easeOut, 1) evaluated at x (CSS-style easing).
function ease01(x: number, easeIn: number, easeOut: number): number {
  const p1x = easeIn
  const p2x = 1 - easeOut
  const cx = 3 * p1x
  const bx = 3 * (p2x - p1x) - cx
  const ax = 1 - cx - bx
  // p1y = 0, p2y = 1 → cy = 0, by = 3, ay = -2.
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t
  const sampleY = (t: number) => (-2 * t + 3) * t * t
  const dX = (t: number) => (3 * ax * t + 2 * bx) * t + cx
  let t = x
  for (let i = 0; i < 5; i++) {
    const d = sampleX(t) - x
    const dd = dX(t)
    if (Math.abs(d) < 1e-4 || Math.abs(dd) < 1e-6) break
    t -= d / dd
  }
  return sampleY(Math.max(0, Math.min(1, t)))
}

// Maps the videoMirror enum to the shader's integer code.
const MIRROR_CODE: Record<string, number> = {
  none: 0,
  horizontal: 1,
  vertical: 2,
  quad: 3,
  kaleido: 4,
}

// Maps the blend mode enum to the recolor shader's integer code.
const BLEND_CODE: Record<string, number> = { normal: 0, multiply: 1, screen: 2, add: 3 }

// Video element with a flag so updateVideoLoop can pause it (ping-pong reverse)
// without the keep-alive 'pause' handler immediately resuming forward playback.
type ManagedVideo = HTMLVideoElement & { __managedPause?: boolean }

/**
 * Manages the Three.js rendering: renderer, camera, controls and the active primitive.
 * The loop reads audio every frame and updates the shared uniforms.
 */
export class Scene {
  private renderer: THREE.WebGLRenderer
  private scene: THREE.Scene
  private camera: THREE.PerspectiveCamera
  private controls: CameraControls
  private clock = new THREE.Clock()
  private freqTex: THREE.DataTexture
  private waveTex: THREE.DataTexture
  private uniforms: SharedUniforms
  private group: THREE.Group
  private primitive: Primitive | null = null
  private rafId = 0
  private readAudio: AudioReader
  private params: VisualParams
  private container: HTMLElement
  private resizeObserver?: ResizeObserver
  private bgVideoEl: HTMLVideoElement | null = null
  private bgTexture: THREE.VideoTexture | null = null
  // Second copy of the background video (same src, time-offset) used only by the
  // 'crossfade' loop style to dissolve over the loop seam.
  private bgVideoElB: HTMLVideoElement | null = null
  private bgTextureB: THREE.VideoTexture | null = null
  // Primitive texture (independent from the background media).
  private texVideoEl: HTMLVideoElement | null = null
  private texTexture: THREE.Texture | null = null
  // Bumped on every setPrimitiveTexture call so a late async load/error callback
  // from a previous source can't activate a texture that has since been replaced.
  private texLoadToken = 0
  // Palette LUT used to recolor the primitive texture (Colors).
  private primLut: THREE.DataTexture | null = null
  // Background video loop-style playback state.
  private pingPhase = true
  private pongAccum = 0
  // Per-beat impulse envelope (0 = just hit, 1 = settled) shared by the
  // acceleration and ping-pong loop styles so both pulse with the song.
  private beatU = 1
  // Musical beat shared across the camera and the video loop: a clean, BPM-gated
  // beat computed once per frame (avoids each consumer re-deriving it).
  private frameMusicalBeat = false
  private beatPeriod = 0.5
  // Last time (ms) the audio had real energy — the beat-reactive video loop only
  // runs while sound is actually playing (a short tail bridges quiet dips).
  private lastLoudMs = 0
  private videoLab: VideoLabRecolor | null = null
  private dummyMedia = createDummyTexture()
  private composer: EffectComposer
  private bloomPass: UnrealBloomPass
  private afterimagePass: AfterimagePass
  // Dual-live transitions: a transient clone of the OUTGOING look (`layerB`) is
  // rendered with the SAME renderer and composited over the incoming look (this
  // scene). Only the primary (non-offscreen) scene owns the compositor + layerB.
  private compositor: TransitionCompositor | null = null
  private layerB: Scene | null = null
  private tx: { cfg: TransitionConfig; start: number; durMs: number } | null = null
  private ownsRenderer = true
  private offscreen = false
  private director = new CameraDirector()
  // Look-around camera used from inside the wrap-around LED shapes (sphere/cyl).
  private insideLook!: InsideLook
  private wasInside = false
  // Camera state: whether the previous frame drove the camera procedurally
  // (cinematic/beat motions) and the running beat-synced phase.
  private wasProcedural = false
  private beatTotal = 0
  private sinceBeat = 0
  private energyTime = 0
  private beatPulse = 0
  // Reactive easing: the audio signals that drive the primitive "push" (bands,
  // beat, per-bin spectrum) run through an asymmetric one-pole envelope so the
  // motion eases IN when energy rises (attack) and eases OUT when it falls
  // (release) instead of snapping each frame, while still pulsing. Because it's
  // a continuous filter it always resumes from the current value, so overlapping
  // beats blend smoothly. Attack/release are user sliders.
  private bassEnv = 0
  private midEnv = 0
  private trebleEnv = 0
  private levelEnv = 0
  private beatEnv = 0
  private freqEnv: Float32Array | null = null
  // Debug: rolling 1s min/max window so the console can show how much each signal
  // actually SWINGS (raw vs eased), not just an instant value.
  private dbgT = 0
  private dbgAcc = {
    bassRawMin: 9, bassRawMax: 0, bassEaseMin: 9, bassEaseMax: 0,
    beatRawMax: 0, beatEaseMax: 0, specRawMax: 0, specEaseMax: 0,
  }
  private dbgWin = { ...this.dbgAcc }
  // Auto-randomize seed: the current auto seed and whether auto was on last
  // frame (so we seed right when it's first enabled).
  private autoSeed = 0
  private wasSeedAuto = false
  // Seed-lobe deformation pulse (0..1): punched on each percussive hit (snare/
  // boom) and decayed each frame. Hits are found via spectral flux (band rising
  // edge), which doesn't saturate on sustained music like the beat detector.
  private seedPulse = 0
  private prevBass = 0
  private prevHigh = 0
  private hitCooldown = 0
  // Zone reaction: per-band onset pulses (bass/mid/treble/level) + flux state.
  private zones!: ZoneReaction
  private zoneConfig: ZoneReactionConfig = DEFAULT_ZONE_REACTION
  private bandPulse = [0, 0, 0, 0]
  // Onset target per band: set on a hit, decays by release; bandPulse eases
  // toward it by attack. This gives the zone push the same attack/release feel.
  private bandTarget = [0, 0, 0, 0]
  private bandPrev = [0, 0, 0, 0]
  private bandCooldown = [0, 0, 0, 0]
  // Reusable buffer: band pulses scaled by reactivity / bass punch, fed to zones.
  private zoneBandScaled = [0, 0, 0, 0]
  private camStep = -1
  private camMode = ''
  private camU = 1
  private camFrom = new THREE.Vector3(0, 0, 7)
  private camTo = new THREE.Vector3(0, 0, 7)
  // Reused scratch for projecting the scene center to screen space (tunnel align).
  private tunnelNdc = new THREE.Vector3()
  private mediaOverlay!: THREE.Mesh
  private sphereBg!: THREE.Mesh
  // Solid/gradient color background (drawn on a 2D canvas → texture → fullscreen quad).
  private bgColorMesh!: THREE.Mesh
  private bgCanvas!: HTMLCanvasElement
  private bgCanvasTex!: THREE.CanvasTexture
  // Mirror + size uniforms for the 360 sphere (injected into MeshBasicMaterial).
  private sphereUniforms = {
    uMirror: { value: 0 },
    uMirrorAmount: { value: 2 },
    uSphereScale: { value: 1 },
  }

  constructor(
    container: HTMLElement,
    params: VisualParams,
    readAudio: AudioReader,
    opts?: { renderer?: THREE.WebGLRenderer; offscreen?: boolean },
  ) {
    this.container = container
    this.params = params
    this.readAudio = readAudio
    this.offscreen = !!opts?.offscreen

    if (opts?.renderer) {
      // Shared GL context (offscreen layer): reuse the primary's renderer so the
      // compositor can sample both layers' render targets in a single draw.
      this.renderer = opts.renderer
      this.ownsRenderer = false
    } else {
      // preserveDrawingBuffer lets captureThumbnail() read the exact on-screen
      // canvas, so thumbnails match the live visual's colors/bloom precisely.
      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        preserveDrawingBuffer: true,
      })
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      this.renderer.setClearColor(0x000000, 1)
      container.appendChild(this.renderer.domElement)
      this.ownsRenderer = true
    }

    this.scene = new THREE.Scene()
    this.scene.fog = new THREE.FogExp2(0x000000, 0.04)

    // Far plane reaches 400 so large wrap-around LED shapes (sphere/cylinder) fit.
    this.camera = new THREE.PerspectiveCamera(60, 1, 0.1, 400)
    this.camera.position.set(0, 0, 7)

    // Offscreen layers bind orbit/look input to a detached element so they never
    // steal pointer/wheel events from the shared on-screen canvas.
    const inputEl = this.offscreen ? document.createElement('canvas') : this.renderer.domElement
    this.controls = new CameraControls(this.camera, inputEl)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.2
    this.insideLook = new InsideLook(inputEl)

    this.freqTex = createFreqTexture()
    this.waveTex = createWaveTexture()
    this.uniforms = createSharedUniforms(this.freqTex, this.waveTex)
    this.uniforms.uPixelRatio.value = this.renderer.getPixelRatio()
    this.zones = new ZoneReaction(this.uniforms)
    this.group = new THREE.Group()
    this.scene.add(this.group)

    // Video overlay "in front" (plane attached to the camera, blacks transparent).
    this.createMediaOverlay()
    this.createSphereBg()
    this.createColorBg()

    this.setPrimitive(params.primitive)

    // Post-processing pipeline: scene -> afterimage (trails) -> bloom -> output.
    const w = container.clientWidth || 1
    const h = container.clientHeight || 1
    this.composer = new EffectComposer(this.renderer)
    this.composer.addPass(new RenderPass(this.scene, this.camera))
    this.afterimagePass = new AfterimagePass(0)
    this.composer.addPass(this.afterimagePass)
    this.bloomPass = new UnrealBloomPass(new THREE.Vector2(w, h), 0.7, 0.5, 0.0)
    this.composer.addPass(this.bloomPass)
    this.composer.addPass(new OutputPass())

    this.resize()

    // Only the primary owns the canvas/container; the offscreen layer is sized
    // explicitly by the primary (see beginTransition / resize).
    if (!this.offscreen) {
      this.resizeObserver = new ResizeObserver(() => this.resize())
      this.resizeObserver.observe(container)
    }
  }

  setParams(params: VisualParams) {
    const densityChanged = params.density !== this.params.density
    const primitiveChanged = params.primitive !== this.params.primitive
    const ledShapeChanged = params.ledShape !== this.params.ledShape
    const sphereRadiusChanged = params.sphereRadius !== this.params.sphereRadius
    const spectrumStyleChanged = params.spectrumStyle !== this.params.spectrumStyle
    const toStatic = params.cameraMode === 'static' && this.params.cameraMode !== 'static'
    this.params = params
    if (primitiveChanged) {
      this.setPrimitive(params.primitive)
    } else if (densityChanged || ledShapeChanged || sphereRadiusChanged || spectrumStyleChanged) {
      this.primitive?.rebuild(params)
    }
    // Primitive turned off -> hide it (no primitive is drawn).
    this.group.visible = params.primitiveEnabled !== false
    this.controls.autoRotate = params.cameraMode === 'autorotate'
    this.controls.autoRotateSpeed = params.rotationSpeed * 0.8
    // Switching to Static returns the camera to its centered default pose (rather
    // than freezing wherever the previous motion left it).
    if (toStatic) this.recenterCamera()
    this.applyMediaLayer()
  }

  private setPrimitive(kind: PrimitiveKind) {
    if (this.primitive) {
      this.group.remove(this.primitive.object3D)
      this.primitive.dispose()
      this.primitive = null
    }
    switch (kind) {
      case 'pointcloud':
        this.primitive = new PointCloud(this.uniforms)
        break
      case 'lines':
        this.primitive = new LineField(this.uniforms)
        break
      case 'triangles':
        this.primitive = new TriangleField(this.uniforms)
        break
      case 'plane':
        this.primitive = new TerrainField(this.uniforms)
        break
      case 'waveform':
        this.primitive = new Waveform(this.uniforms)
        break
      case 'constellation':
        this.primitive = new Constellation(this.uniforms)
        break
      case 'tunnel':
        this.primitive = new Tunnel(this.uniforms)
        break
      case 'vortex':
        this.primitive = new Vortex(this.uniforms)
        break
      case 'ledpanel':
        this.primitive = new LedPanel(this.uniforms)
        break
      case 'fluid':
        this.primitive = new Water(this.uniforms)
        break
      case 'fireworks':
        this.primitive = new Fireworks(this.uniforms)
        break
      case 'rain':
        this.primitive = new ProjectileRain(this.uniforms)
        break
      case 'smoke':
        this.primitive = new Smoke(this.uniforms)
        break
      case 'plasma':
        this.primitive = new Plasma(this.uniforms)
        break
      case 'metaballs':
        this.primitive = new Metaballs(this.uniforms)
        break
      case 'spectrum':
        this.primitive = new Spectrum(this.uniforms)
        break
      case 'galaxy':
        this.primitive = new Galaxy(this.uniforms)
        break
      case 'aurora':
        this.primitive = new Aurora(this.uniforms)
        break
      case 'ferro':
        this.primitive = new Ferro(this.uniforms)
        break
      case 'videodisplace':
        this.primitive = new VideoDisplaceField(this.uniforms)
        break
    }
    if (this.primitive) {
      this.primitive.rebuild(this.params)
      this.group.add(this.primitive.object3D)
    }
    this.applyMediaLayer()
  }

  private createMediaOverlay() {
    const mat = new THREE.ShaderMaterial({
      uniforms: this.uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        // Fullscreen clip-space quad: independent of the camera (position, rotation,
        // zoom, projection), so the video always fills the screen and stays centered.
        void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform sampler2D uMedia;
        uniform sampler2D uMedia2;
        uniform float uLoopFade;
        uniform float uMediaActive;
        uniform float uMediaFull;
        uniform float uMediaAspect;
        uniform float uScreenAspect;
        varying vec2 vUv;
        uniform float uMediaOpacity;
        uniform float uTunnel;
        uniform float uTunnelAngle;
        uniform float uTunnelDepth;
        uniform vec2 uTunnelShift;
        uniform float uTime;
        ${MIRROR_GLSL}
        // Crossfade between the video and its offset copy near the loop seam, so
        // the jump from end back to start becomes a dissolve.
        vec4 sampleMedia(vec2 uv) {
          vec4 a = texture2D(uMedia, uv);
          if (uLoopFade <= 0.001) return a;
          return mix(a, texture2D(uMedia2, uv), uLoopFade);
        }
        void main() {
          if (uMediaActive < 0.5) discard;
          // Infinite tunnel: polar mapping with an animated depth so the texture
          // scrolls toward the viewer (center = far away). The camera drives it:
          // orbiting spins it (uTunnelAngle), dollying moves through it
          // (uTunnelDepth) and looking around pans the vanishing point (uTunnelShift).
          if (uTunnel > 0.5) {
            vec2 q = vUv - 0.5;
            q.x *= uScreenAspect;
            q -= uTunnelShift;
            float ang = atan(q.y, q.x) + uTunnelAngle;
            float rad = max(length(q), 1e-3);
            vec2 tuv = vec2(ang / 6.28318530718 + 0.5, 0.30 / rad + uTime * 0.35 + uTunnelDepth);
            tuv = applyMirror(tuv, 1.0);
            gl_FragColor = vec4(sampleMedia(tuv).rgb, uMediaOpacity);
            return;
          }
          // Fold the SCREEN space first (seamless, symmetric reflections), then
          // cover-fit each mirrored tile. This is what makes the seams close.
          vec2 m = applyMirror(vUv, uScreenAspect);
          if (uMediaFull > 0.5) {
            // Fullscreen "cover": expand from the center to fill the tile preserving
            // aspect, cropping the overflow (no black bars).
            vec2 uv = m;
            if (uScreenAspect > uMediaAspect) {
              uv.y = (m.y - 0.5) * (uMediaAspect / uScreenAspect) + 0.5;
            } else {
              uv.x = (m.x - 0.5) * (uScreenAspect / uMediaAspect) + 0.5;
            }
            gl_FragColor = vec4(sampleMedia(uv).rgb, uMediaOpacity);
            return;
          }
          vec3 c = sampleMedia(m).rgb;
          // Luminance key: blacks become transparent (the shape shows through).
          float a = clamp(dot(c, vec3(0.299, 0.587, 0.114)) * 1.3, 0.0, 1.0);
          gl_FragColor = vec4(c, a * uMediaOpacity);
        }
      `,
    })
    // Fullscreen quad (clip-space, see vertex shader): -1..1 maps to the whole
    // screen, so it doesn't depend on the camera. Added straight to the scene.
    this.mediaOverlay = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat)
    this.mediaOverlay.frustumCulled = false
    this.mediaOverlay.renderOrder = 999
    this.mediaOverlay.visible = false
    this.scene.add(this.mediaOverlay)
  }

  /**
   * Large inward-facing sphere used for the 360 background: the video is mapped
   * (equirectangular) on the inside, the camera sits within it, and orbiting the
   * camera looks around it. fog:false so the distant sphere isn't fogged to black.
   */
  private createSphereBg() {
    const mat = new THREE.MeshBasicMaterial({
      side: THREE.BackSide,
      fog: false,
      toneMapped: false,
    })
    // Inject the mirror effect into the equirectangular UV of the map, keeping
    // three.js's own color management (sRGB / video decode) intact.
    // NOTE: onBeforeCompile runs BEFORE #include directives are resolved, so we
    // replace the `#include <map_fragment>` line itself (not the expanded code).
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uMirror = this.sphereUniforms.uMirror
      shader.uniforms.uMirrorAmount = this.sphereUniforms.uMirrorAmount
      shader.uniforms.uSphereScale = this.sphereUniforms.uSphereScale
      shader.fragmentShader =
        'uniform float uSphereScale;\n' +
        MIRROR_GLSL +
        '\n' +
        shader.fragmentShader.replace(
          '#include <map_fragment>',
          /* glsl */ `
          #ifdef USE_MAP
            vec2 sphUv = ( vMapUv - 0.5 ) * uSphereScale + 0.5;
            vec4 sampledDiffuseColor = texture2D( map, applyMirror( sphUv, 2.0 ) );
            #ifdef DECODE_VIDEO_TEXTURE
              sampledDiffuseColor = vec4( mix( pow( sampledDiffuseColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), sampledDiffuseColor.rgb * 0.0773993808, vec3( lessThanEqual( sampledDiffuseColor.rgb, vec3( 0.04045 ) ) ) ), sampledDiffuseColor.w );
            #endif
            diffuseColor *= sampledDiffuseColor;
          #endif
          `,
        )
    }
    this.sphereBg = new THREE.Mesh(new THREE.SphereGeometry(50, 64, 40), mat)
    this.sphereBg.visible = false
    this.scene.add(this.sphereBg)
  }

  /** Fullscreen quad showing a solid/gradient color (from a 2D canvas texture). */
  private createColorBg() {
    const canvas = document.createElement('canvas')
    canvas.width = 512
    canvas.height = 512
    this.bgCanvas = canvas
    const tex = new THREE.CanvasTexture(canvas)
    tex.colorSpace = THREE.SRGBColorSpace
    this.bgCanvasTex = tex
    const mat = new THREE.ShaderMaterial({
      uniforms: { uColorBg: { value: tex } },
      depthTest: false,
      depthWrite: false,
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform sampler2D uColorBg;
        varying vec2 vUv;
        void main() { gl_FragColor = vec4(texture2D(uColorBg, vUv).rgb, 1.0); }
      `,
    })
    this.bgColorMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat)
    this.bgColorMesh.frustumCulled = false
    this.bgColorMesh.renderOrder = -10 // behind everything
    this.bgColorMesh.visible = false
    this.scene.add(this.bgColorMesh)
  }

  /** Redraws the color background (solid or linear/radial multi-stop gradient). */
  setColorBackground(mode: string, type: string, angle: number, stops: string[]) {
    const ctx = this.bgCanvas.getContext('2d')
    if (!ctx) return
    const w = this.bgCanvas.width
    const h = this.bgCanvas.height
    ctx.clearRect(0, 0, w, h)
    const list = stops.length ? stops : ['#000000']
    if (mode === 'solid' || list.length < 2) {
      ctx.fillStyle = list[0]
      ctx.fillRect(0, 0, w, h)
    } else {
      let grad: CanvasGradient
      if (type === 'radial') {
        grad = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.5)
      } else {
        const a = ((angle ?? 0) * Math.PI) / 180
        const r = Math.max(w, h) * 0.5
        const dx = Math.cos(a) * r
        const dy = Math.sin(a) * r
        grad = ctx.createLinearGradient(w / 2 - dx, h / 2 - dy, w / 2 + dx, h / 2 + dy)
      }
      const n = list.length
      list.forEach((c, i) => grad.addColorStop(n === 1 ? 0 : i / (n - 1), c))
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, w, h)
    }
    this.bgCanvasTex.needsUpdate = true
  }

  /**
   * Drives the background video's playback style each frame:
   *  - 'linear'       : normal forward loop at 1x.
   *  - 'acceleration' : speeds up on each beat then eases back (subtle decel).
   *  - 'pingpong'     : steps currentTime forward/backward (browsers can't play
   *                     a <video> in reverse), beat-driven speed, bounces at ends.
   */
  private updateVideoLoop(dt: number, level: number) {
    const v = (this.videoLab?.active ? this.videoLab.videoElement : null) ?? this.bgVideoEl
    if (!v) return
    const style = this.params.videoLoopStyle ?? 'linear'
    const p = this.params
    // Audio activity: the beat-reactive styles only run while sound is actually
    // playing. A short tail (350 ms) bridges quiet dips so it doesn't flicker.
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    if (level > 0.02) this.lastLoudMs = now
    const audioActive = now - this.lastLoudMs < 350
    // Each musical beat re-triggers the shared impulse envelope (beatU).
    if (audioActive && this.frameMusicalBeat) this.beatU = 0

    if (style === 'crossfade') {
      // Seamless loop: a second decoder (B) of the same clip is kept `blend`
      // seconds AHEAD of A; near A's loop seam we dissolve A→B so the cut from
      // end back to start becomes a fade. Both play forward natively (smooth);
      // B is only re-synced while it's hidden (fade ≈ 0) to avoid seek stutter.
      const a = this.bgVideoEl
      const b = this.bgVideoElB
      if (!a || !b) {
        this.uniforms.uLoopFade.value = 0
        return
      }
      const dur = a.duration
      if (!dur || !isFinite(dur)) {
        this.uniforms.uLoopFade.value = 0
        return
      }
      a.loop = true
      b.loop = true
      ;(a as ManagedVideo).__managedPause = false
      ;(b as ManagedVideo).__managedPause = false
      if (a.paused) void a.play().catch(() => {})
      if (b.paused) void b.play().catch(() => {})
      a.playbackRate += (1 - a.playbackRate) * 0.1
      b.playbackRate = a.playbackRate
      const blend = Math.max(0.1, Math.min(dur * 0.45, p.loopBlend))
      const d = Math.min(a.currentTime, dur - a.currentTime) // distance to seam
      const w = Math.max(0, Math.min(1, 1 - d / blend))
      const targetB = (a.currentTime + blend) % dur
      const diff = Math.abs(b.currentTime - targetB)
      const drift = Math.min(diff, dur - diff)
      if (drift > 0.08 && w < 0.12) {
        try {
          b.currentTime = targetB
        } catch {
          /* seeking may throw if not ready */
        }
      }
      this.uniforms.uLoopFade.value = w
      return
    }
    // Any other style: turn the crossfade dissolve off and stop the second
    // decoder so it doesn't run needlessly.
    this.uniforms.uLoopFade.value = 0
    if (this.bgVideoElB && !this.bgVideoElB.paused) {
      ;(this.bgVideoElB as ManagedVideo).__managedPause = true
      this.bgVideoElB.pause()
    }

    if (style === 'pingpong') {
      const dur = v.duration
      if (!dur || !isFinite(dur)) return
      const mv = v as ManagedVideo

      // No music → don't ping-pong; play forward normally (so it's not frozen and
      // there's no endless back-and-forth in silence).
      if (!audioActive) {
        mv.__managedPause = false
        v.loop = true
        if (v.paused) void v.play().catch(() => {})
        v.playbackRate += (1 - v.playbackRate) * 0.1
        this.pingPhase = true
        this.pongAccum = 0
        return
      }

      // Forward then backward, bouncing at the video ends. The scrub speed pulses
      // on each musical beat (beatU over one beat period) so the motion locks to
      // the song; pingSpeed/pongSpeed are the peaks, pingEase/pongEase the shape.
      v.loop = false
      this.beatU = Math.min(1, this.beatU + dt / Math.max(0.05, this.beatPeriod))
      const speed = this.pingPhase ? p.pingSpeed : p.pongSpeed
      const ease = this.pingPhase ? p.pingEase : p.pongEase
      const rate = Math.min(16, Math.max(0.1, speed * (0.2 + 0.8 * vProfile(this.beatU, ease))))

      if (this.pingPhase) {
        // Forward: native playback (smooth — no per-frame seeking).
        mv.__managedPause = false
        if (v.paused) void v.play().catch(() => {})
        v.playbackRate = rate
        if (v.currentTime >= dur - 0.05) {
          this.pingPhase = false
          this.pongAccum = 0
        }
      } else {
        // Backward: browsers can't play a <video> in reverse, so we seek. Pause it
        // and flag the pause as MANAGED so the keep-alive 'pause' handler won't
        // resume forward playback (that fight was the visible jump).
        mv.__managedPause = true
        if (!v.paused) v.pause()
        this.pongAccum += rate * dt
        if (this.pongAccum >= 1 / 24) {
          let t = v.currentTime - this.pongAccum
          this.pongAccum = 0
          if (t <= 0) {
            t = 0
            this.pingPhase = true
            mv.__managedPause = false
          }
          try {
            v.currentTime = t
          } catch {
            /* seeking may throw if not ready */
          }
        }
      }
      return
    }

    // linear / acceleration: forward, looping. Clear any managed pause a previous
    // ping-pong pass may have left so the video resumes.
    ;(v as ManagedVideo).__managedPause = false
    if (v.paused) void v.play().catch(() => {})
    v.loop = true
    if (style === 'acceleration' && audioActive) {
      // One eased speed impulse on each musical beat: peak accelSpeed over
      // accelDuration seconds, shaped by accelEase, then it settles back to 1x.
      if (this.beatU < 1) {
        this.beatU = Math.min(1, this.beatU + dt / Math.max(0.05, p.accelDuration))
        const e = vProfile(this.beatU, p.accelEase)
        v.playbackRate = Math.min(16, Math.max(0.1, 1 + (p.accelSpeed - 1) * e))
      } else {
        v.playbackRate += (1 - v.playbackRate) * 0.2 // settle back to 1x
      }
    } else {
      // linear, or acceleration with no music → ease back to 1x.
      v.playbackRate += (1 - v.playbackRate) * 0.1
    }
  }

  /**
   * Routes the active media. Source: the Video Lab's recolored video takes
   * priority over the flat video/image (bgTexture). The video is ALWAYS fed to
   * the primitive via uMedia (so the LED works), independently of the background.
   * The background placement is controlled by `videoBackground`:
   *   - 'off'    : no background drawn (the primitive can still use the video).
   *   - 'flat'   : fullscreen clip-space quad. 'front' = keyed overlay on top of
   *                the primitive (blacks transparent); 'back' = opaque "cover" behind.
   *   - 'sphere' : equirectangular 360 sphere the camera looks around.
   * Background and primitive are independent: you can have both at once.
   */
  private applyMediaLayer() {
    const media: THREE.Texture | null = this.videoLab?.active
      ? this.videoLab.texture
      : this.bgTexture
    const primitiveOff = this.params.primitiveEnabled === false
    const background = this.params.videoBackground ?? 'flat'
    const front = this.params.videoLayer === 'front'
    const opacity = this.params.videoOpacity ?? 1
    const mat = this.mediaOverlay.material as THREE.ShaderMaterial
    // Tunnel mode reuses the flat quad with an animated polar mapping.
    this.uniforms.uTunnel.value = background === 'tunnel' ? 1 : 0

    this.uniforms.uMedia.value = media ?? this.dummyMedia
    this.uniforms.uMediaActive.value = media ? 1 : 0
    this.scene.background = null

    // MirroredRepeat wrap so the mirror/kaleidoscope effects always stitch
    // seamlessly (the texture itself reflects at every boundary; out-of-range
    // folded coordinates never produce a hard seam or clamped smear).
    if (media && media.wrapS !== THREE.MirroredRepeatWrapping) {
      media.wrapS = THREE.MirroredRepeatWrapping
      media.wrapT = THREE.MirroredRepeatWrapping
      media.needsUpdate = true
    }

    // Keep the mirror/kaleidoscope + opacity uniforms (flat quad + sphere) in sync.
    this.applyVideoFx()

    // Default: hide the flat quad, the 360 sphere and the color background.
    this.mediaOverlay.visible = false
    this.sphereBg.visible = false
    this.bgColorMesh.visible = (this.params.backgroundType ?? 'video') === 'color'

    if (!media || background === 'off') return

    // 360 background: equirectangular sphere the camera looks around.
    if (background === 'sphere') {
      const sm = this.sphereBg.material as THREE.MeshBasicMaterial
      if (sm.map !== media) {
        sm.map = media
        sm.needsUpdate = true
      }
      sm.opacity = opacity
      sm.transparent = opacity < 0.999
      this.sphereBg.visible = true
      return
    }

    // Flat background: 'front' = keyed overlay on top; otherwise opaque "cover" behind.
    const keyed = front && !primitiveOff
    const wantTransparent = keyed || opacity < 0.999
    if (mat.transparent !== wantTransparent) {
      mat.transparent = wantTransparent
      mat.needsUpdate = true
    }
    this.uniforms.uMediaFull.value = keyed ? 0 : 1
    this.mediaOverlay.renderOrder = !keyed && !primitiveOff ? -1 : 999
    this.mediaOverlay.visible = true
    if (!keyed) this.updateScreenMediaAspect()
  }

  /** Syncs the mirror/kaleidoscope + sphere-size uniforms from the params. */
  private applyVideoFx() {
    const code = MIRROR_CODE[this.params.videoMirror ?? 'none'] ?? 0
    const amount = this.params.videoMirrorAmount ?? 2
    this.uniforms.uMirror.value = code
    this.uniforms.uMirrorAmount.value = amount
    this.sphereUniforms.uMirror.value = code
    this.sphereUniforms.uMirrorAmount.value = amount
    this.sphereUniforms.uSphereScale.value = this.params.videoSphereScale ?? 1
    this.uniforms.uMediaOpacity.value = this.params.videoOpacity ?? 1
    // Texture inside the primitive's pixels (screen-space) when a source is picked.
    const textured = (this.params.textureSource ?? 'none') !== 'none'
    this.uniforms.uTexturePrimitive.value = textured ? 1 : 0
    if (textured) this.updateScreenMediaAspect()
  }

  /** Updates the aspect ratios (video and viewport) used by the fullscreen "cover". */
  private updateScreenMediaAspect() {
    this.uniforms.uScreenAspect.value = this.camera.aspect
    if (this.videoLab?.active) {
      this.uniforms.uMediaAspect.value = this.videoLab.aspect
      return
    }
    const img = this.bgTexture?.image as
      | { videoWidth?: number; videoHeight?: number; width?: number; height?: number }
      | undefined
    const w = img?.videoWidth || img?.width || 0
    const h = img?.videoHeight || img?.height || 0
    if (w > 0 && h > 0) this.uniforms.uMediaAspect.value = w / h
  }

  /**
   * Aligns the tunnel background with the 3D scene so its vanishing point tracks
   * the primitive's. The primitive sits at the origin and the camera looks at it,
   * so the origin's screen projection IS the primitive's perspective vanishing
   * point; we anchor the tunnel center there. As the camera moves (orbit, pan,
   * beat motions) both recede toward the same point. Dolly scrolls through the
   * tunnel; no roll is applied (the scene camera is roll-free).
   */
  private updateTunnelCamera() {
    const cam = this.camera
    // Project the scene center (origin) to NDC. updateMatrixWorld() also refreshes
    // matrixWorldInverse (Camera override), which project() needs and the renderer
    // hasn't computed yet this frame.
    cam.updateMatrixWorld()
    this.tunnelNdc.set(0, 0, 0).project(cam)
    // Map NDC (-1..1) to the shader's q-space: q = vUv-0.5 with q.x *= aspect, and
    // vUv = ndc*0.5+0.5, so the tunnel center lands exactly on the origin on screen.
    this.uniforms.uTunnelShift.value.set(
      this.tunnelNdc.x * 0.5 * cam.aspect,
      this.tunnelNdc.y * 0.5,
    )
    // Dolly (zoom) moves through the tunnel (7 = default distance).
    this.uniforms.uTunnelDepth.value = (7 - cam.position.length()) * 0.2
    this.uniforms.uTunnelAngle.value = 0
  }

  /**
   * Selects the flat media (video/image): used as the scene background AND as the
   * source for LED-screen-style primitives (uniform uMedia).
   */
  setBackgroundVideo(url: string | null) {
    // Clear the previous one.
    if (this.bgTexture) {
      this.bgTexture.dispose()
      this.bgTexture = null
    }
    if (this.bgVideoEl) {
      this.bgVideoEl.pause()
      this.bgVideoEl.src = ''
      this.bgVideoEl.remove()
      this.bgVideoEl = null
    }
    // Clear the crossfade copy too.
    if (this.bgTextureB) {
      this.bgTextureB.dispose()
      this.bgTextureB = null
    }
    if (this.bgVideoElB) {
      this.bgVideoElB.pause()
      this.bgVideoElB.src = ''
      this.bgVideoElB.remove()
      this.bgVideoElB = null
    }
    this.uniforms.uMedia2.value = this.dummyMedia
    this.uniforms.uLoopFade.value = 0
    this.applyMediaLayer()
    if (!url) return

    const setTex = (tex: THREE.Texture) => {
      tex.colorSpace = THREE.SRGBColorSpace
      this.bgTexture = tex as THREE.VideoTexture
      this.applyMediaLayer()
    }

    const isVideo = /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(url) || url.startsWith('blob:')
    if (isVideo) {
      const video = document.createElement('video')
      video.src = url
      video.crossOrigin = 'anonymous'
      video.loop = true
      video.muted = true
      video.autoplay = true
      video.playsInline = true
      video.preload = 'auto'
      // Hidden but IN the DOM: prevents the browser from pausing/throttling the
      // decoding of a non-visible video (it plays in full and loops).
      video.style.cssText =
        'position:fixed;left:-10px;top:-10px;width:1px;height:1px;opacity:0;pointer-events:none;'
      document.body.appendChild(video)
      const keepPlaying = () => void video.play().catch(() => {})
      // If the browser pauses it (throttling), we resume it — UNLESS the pause is
      // managed (ping-pong reverse intentionally pauses to seek backward).
      video.addEventListener('pause', () => {
        if (video.isConnected && !video.ended && !(video as ManagedVideo).__managedPause) {
          keepPlaying()
        }
      })
      keepPlaying()
      const tex = new THREE.VideoTexture(video)
      this.bgVideoEl = video
      setTex(tex)

      // Crossfade copy: a second decoder of the same clip, kept time-offset and
      // dissolved in around the loop seam (driven in updateVideoLoop). Created
      // for every video; it only contributes when the loop style is 'crossfade'.
      const videoB = document.createElement('video')
      videoB.src = url
      videoB.crossOrigin = 'anonymous'
      videoB.loop = true
      videoB.muted = true
      videoB.autoplay = true
      videoB.playsInline = true
      videoB.preload = 'auto'
      videoB.style.cssText =
        'position:fixed;left:-10px;top:-10px;width:1px;height:1px;opacity:0;pointer-events:none;'
      document.body.appendChild(videoB)
      const keepPlayingB = () => void videoB.play().catch(() => {})
      videoB.addEventListener('pause', () => {
        // Stays paused when intentionally stopped (non-crossfade styles), so the
        // second decoder doesn't run unless the crossfade needs it.
        if (videoB.isConnected && !videoB.ended && !(videoB as ManagedVideo).__managedPause) {
          keepPlayingB()
        }
      })
      ;(videoB as ManagedVideo).__managedPause = true
      videoB.pause()
      const texB = new THREE.VideoTexture(videoB)
      texB.colorSpace = THREE.SRGBColorSpace
      this.bgVideoElB = videoB
      this.bgTextureB = texB
      this.uniforms.uMedia2.value = texB
    } else {
      new THREE.TextureLoader().load(url, setTex)
    }
  }

  /**
   * Sets the texture painted inside the primitive's pixels (video or image).
   * Independent from the background media (its own element/texture).
   */
  setPrimitiveTexture(url: string | null) {
    // Invalidate any in-flight load from a previous source.
    const token = ++this.texLoadToken
    if (this.texTexture) {
      this.texTexture.dispose()
      this.texTexture = null
    }
    if (this.texVideoEl) {
      this.texVideoEl.pause()
      this.texVideoEl.src = ''
      this.texVideoEl.remove()
      this.texVideoEl = null
    }
    // Default to inactive until the source actually loads. A texture that never
    // loads (e.g. a 404/500 video) must NOT stay "active": an empty/black
    // VideoTexture would otherwise paint every texturable primitive solid black.
    this.uniforms.uPrimitiveMedia.value = this.dummyMedia
    this.uniforms.uPrimitiveMediaActive.value = 0
    if (!url) return

    const activate = (tex: THREE.Texture) => {
      // A newer load (or a clear) superseded this one — ignore the late callback.
      if (token !== this.texLoadToken) return
      tex.colorSpace = THREE.SRGBColorSpace
      this.texTexture = tex
      this.uniforms.uPrimitiveMedia.value = tex
      this.uniforms.uPrimitiveMediaActive.value = 1
    }
    const isVideo = /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(url) || url.startsWith('blob:')
    if (isVideo) {
      const video = document.createElement('video')
      video.src = url
      video.crossOrigin = 'anonymous'
      video.loop = true
      video.muted = true
      video.autoplay = true
      video.playsInline = true
      video.preload = 'auto'
      video.style.cssText =
        'position:fixed;left:-10px;top:-10px;width:1px;height:1px;opacity:0;pointer-events:none;'
      document.body.appendChild(video)
      const keepPlaying = () => void video.play().catch(() => {})
      video.addEventListener('pause', () => {
        if (video.isConnected && !video.ended) keepPlaying()
      })
      keepPlaying()
      this.texVideoEl = video
      const tex = new THREE.VideoTexture(video)
      // Only activate once the video has real frame data; a failed load leaves
      // the texture inactive so the primitive keeps its own color (no black-out).
      video.addEventListener('loadeddata', () => activate(tex))
      video.addEventListener('error', () => {
        if (token === this.texLoadToken) this.uniforms.uPrimitiveMediaActive.value = 0
      })
    } else {
      new THREE.TextureLoader().load(
        url,
        activate,
        undefined,
        () => {
          if (token === this.texLoadToken) this.uniforms.uPrimitiveMediaActive.value = 0
        },
      )
    }
  }

  /**
   * Recolor for the primitive texture (Colors): builds/updates the palette LUT
   * and the levels so the primitive's video/image is remapped to the palette.
   */
  setPrimitiveRecolor(active: boolean, a: VideoLabAppearance) {
    if (this.primLut) buildPaletteLUT(a.palette, a.bands, a.smooth, this.primLut)
    else this.primLut = buildPaletteLUT(a.palette, a.bands, a.smooth)
    this.uniforms.uReLUT.value = this.primLut
    this.uniforms.uReActive.value = active ? 1 : 0
    this.uniforms.uReIntensity.value = a.intensity
    this.uniforms.uReBlack.value = a.black
    this.uniforms.uReWhite.value = a.white
    this.uniforms.uReBlend.value = BLEND_CODE[a.blend] ?? 0
  }

  /**
   * Video Lab: loads/removes the grayscale video that is recolored live.
   * Its output (recolored texture) becomes the background and the LED source.
   */
  setVideoLabSource(url: string | null, appearance: VideoLabAppearance) {
    if (!url) {
      this.videoLab?.setSource(null)
      this.applyMediaLayer()
      return
    }
    if (!this.videoLab) {
      this.videoLab = new VideoLabRecolor(appearance)
    } else {
      this.videoLab.setAppearance(appearance)
    }
    this.videoLab.setSource(url)
    this.applyMediaLayer()
  }

  /** Updates the Video Lab palette/bands/intensity without reloading the video. */
  setVideoLabAppearance(appearance: VideoLabAppearance) {
    this.videoLab?.setAppearance(appearance)
  }

  /**
   * Clean, BPM-gated musical beat, computed ONCE per frame and shared by every
   * consumer (camera + video loop). Locks to the estimated tempo and confirms
   * with the bass-energy beat, gating by ~half a beat so bass noise can't trigger
   * extras; if the bass beat is missed, it falls back to the BPM grid (~1.6 beats).
   */
  private updateMusicalBeat(frame: AudioFrame, dt: number) {
    const bpm = Math.max(40, Math.min(220, frame.bpm || 120))
    this.beatPeriod = 60 / bpm
    this.sinceBeat += dt
    this.frameMusicalBeat = false
    if (
      (frame.beat && this.sinceBeat >= this.beatPeriod * 0.55) ||
      this.sinceBeat >= this.beatPeriod * 1.6
    ) {
      this.frameMusicalBeat = true
      this.beatTotal += 1
      this.sinceBeat = 0
    }
  }

  /**
   * Smooths all the reactive signals the primitives read for their "push" so the
   * motion eases in and out (controlled by the Beat attack / Beat release
   * sliders) instead of snapping every frame. Overwrites the uniforms (and the
   * spectrum texture) that `updateUniforms` just filled with raw values.
   *
   * Asymmetric one-pole: rising uses the attack factor, falling uses release.
   */
  private ease1(env: number, target: number, kA: number, kR: number): number {
    return env + (target - env) * (target > env ? kA : kR)
  }

  private applyReactiveEasing(frame: AudioFrame, dt: number) {
    const attack = Math.max(0.005, Math.min(0.4, this.params.beatAttack))
    const release = Math.max(0.04, Math.min(0.8, this.params.beatRelease))
    // Per-frame smoothing factors from the time constants (~63% per tau seconds).
    const kA = 1 - Math.exp(-dt / attack)
    const kR = 1 - Math.exp(-dt / release)

    this.bassEnv = this.ease1(this.bassEnv, frame.bass, kA, kR)
    this.midEnv = this.ease1(this.midEnv, frame.mid, kA, kR)
    this.trebleEnv = this.ease1(this.trebleEnv, frame.treble, kA, kR)
    this.levelEnv = this.ease1(this.levelEnv, frame.level, kA, kR)
    audioEnergy = this.levelEnv
    this.beatEnv = this.ease1(this.beatEnv, frame.beatIntensity, kA, kR)
    this.uniforms.uBass.value = this.bassEnv
    this.uniforms.uMid.value = this.midEnv
    this.uniforms.uTreble.value = this.trebleEnv
    this.uniforms.uLevel.value = this.levelEnv
    this.uniforms.uBeat.value = this.beatEnv

    // Per-bin spectrum easing: this is what makes the radial spikes (Lines) and
    // other spectrum-driven shapes grow/shrink smoothly rather than flicker.
    const data = this.freqTex.image.data as Uint8Array
    if (!this.freqEnv || this.freqEnv.length !== data.length) {
      this.freqEnv = new Float32Array(data.length)
    }
    const env = this.freqEnv
    let specRawMax = 0
    let specEaseMax = 0
    for (let i = 0; i < data.length; i++) {
      const target = data[i]
      if (target > specRawMax) specRawMax = target
      env[i] += (target - env[i]) * (target > env[i] ? kA : kR)
      data[i] = env[i]
      if (env[i] > specEaseMax) specEaseMax = env[i]
    }
    this.freqTex.needsUpdate = true

    // Track the rolling 1s min/max so the console shows how much each signal
    // SWINGS (raw vs eased): if raw swings wide but eased stays narrow, the
    // easing is working; if both swing the same, it isn't reaching the visual.
    const a = this.dbgAcc
    a.bassRawMin = Math.min(a.bassRawMin, frame.bass)
    a.bassRawMax = Math.max(a.bassRawMax, frame.bass)
    a.bassEaseMin = Math.min(a.bassEaseMin, this.bassEnv)
    a.bassEaseMax = Math.max(a.bassEaseMax, this.bassEnv)
    a.beatRawMax = Math.max(a.beatRawMax, frame.beatIntensity)
    a.beatEaseMax = Math.max(a.beatEaseMax, this.beatEnv)
    a.specRawMax = Math.max(a.specRawMax, specRawMax / 255)
    a.specEaseMax = Math.max(a.specEaseMax, specEaseMax / 255)
    this.dbgT += dt
    if (this.dbgT >= 1) {
      this.dbgWin = { ...a }
      this.dbgT = 0
      a.bassRawMin = 9; a.bassRawMax = 0; a.bassEaseMin = 9; a.bassEaseMax = 0
      a.beatRawMax = 0; a.beatEaseMax = 0; a.specRawMax = 0; a.specEaseMax = 0
    }
    const w = this.dbgWin

    // Publish a live snapshot for the on-screen debug console.
    sceneDebug = {
      primitive: this.params.primitive,
      'attack used (s)': r3(attack),
      'release used (s)': r3(release),
      'bass raw (now)': r2(frame.bass),
      'bass eased (now)': r2(this.bassEnv),
      'bass raw swing (1s)': `${r2(w.bassRawMin)}–${r2(w.bassRawMax)}`,
      'bass eased swing (1s)': `${r2(w.bassEaseMin)}–${r2(w.bassEaseMax)}`,
      'beat raw max (1s)': r2(w.beatRawMax),
      'beat eased max (1s)': r2(w.beatEaseMax),
      'spectrum raw max (1s)': r2(w.specRawMax),
      'spectrum eased max (1s)': r2(w.specEaseMax),
      seedAuto: this.params.seedAuto ? 'on' : 'off',
      seedPulse: r2(this.seedPulse),
      'zone reaction': this.zoneConfig.enabled ? 'on' : 'off',
      'zone pulses': this.bandPulse.map(r2).join(' '),
      reactivity: r2(this.params.reactivity),
      'bass punch': r2(this.params.bassPunch),
      // Crossfade loop diagnostics.
      'loop style': this.params.videoLoopStyle ?? 'linear',
      loopFade: r2(this.uniforms.uLoopFade.value),
      'bg video A': this.bgVideoEl ? r2(this.bgVideoEl.currentTime) : 'none',
      'bg video B': this.bgVideoElB
        ? `${r2(this.bgVideoElB.currentTime)}${this.bgVideoElB.paused ? ' (paused)' : ''}`
        : 'none',
      'videoLab recolor': this.videoLab?.active ? 'on (no crossfade)' : 'off',
    }
  }

  /**
   * Per-band onset pulses (bass/mid/treble/level) via spectral flux (a rising
   * edge in the band), debounced per band. Unlike a ratio-to-average threshold,
   * flux keeps firing on sustained music. Feeds Zone reaction's local deform.
   *
   * Each hit sets a per-band TARGET that decays over `release`; the pulse eases
   * toward that target over `attack`. So the zone push eases IN and OUT (driven
   * by the Beat attack/release sliders) instead of snapping, and overlapping
   * hits resume from the current value instead of jumping.
   */
  private updateBandPulses(frame: AudioFrame, dt: number) {
    // Per-band sensitivity gain compensates the spectral rolloff: high bands
    // (mid/treble) carry far less energy than bass, so without this they barely
    // react. The gained value is clamped to 1 so loud bass still tops out.
    const gain = [1.0, 1.9, 3.5, 1.6]
    const vals = [
      Math.min(1, frame.bass * gain[0]),
      Math.min(1, frame.mid * gain[1]),
      Math.min(1, frame.treble * gain[2]),
      Math.min(1, frame.level * gain[3]),
    ]
    const fluxThresh = [0.05, 0.035, 0.03, 0.04]
    const floor = [0.2, 0.12, 0.1, 0.14]
    const attack = Math.max(0.005, Math.min(0.4, this.params.beatAttack))
    const release = Math.max(0.04, Math.min(0.8, this.params.beatRelease))
    const kA = 1 - Math.exp(-dt / attack)
    const kR = 1 - Math.exp(-dt / release)
    // The continuous baseline scales with release: at MIN release there's no
    // baseline (snappy, punch-only); at MAX it's full (smooth, always reacting).
    const baselineAmt = ((release - 0.04) / (0.8 - 0.04)) * 0.7
    for (let k = 0; k < 4; k++) {
      this.bandCooldown[k] = Math.max(0, this.bandCooldown[k] - dt)
      const flux = vals[k] - this.bandPrev[k]
      this.bandPrev[k] = vals[k]
      // Onset → raise the target (resume from current); otherwise the target
      // decays (ease-out).
      if (flux > fluxThresh[k] && vals[k] > floor[k] && this.bandCooldown[k] <= 0) {
        this.bandCooldown[k] = 0.11
        this.bandTarget[k] = Math.max(this.bandTarget[k], Math.min(1, 0.55 + flux * 3.0))
      } else {
        this.bandTarget[k] = this.bandTarget[k] * (1 - kR)
      }
      // Continuous floor from the band's energy so the primitive keeps reacting
      // during sustained passages (pure spectral flux drops to ~0 when the level
      // is steady, which made the bands look like they "stopped"). Onsets still
      // punch ABOVE this floor.
      const baseline = Math.max(0, Math.min(1, (vals[k] - floor[k]) / (1 - floor[k]))) * baselineAmt
      this.bandTarget[k] = Math.max(this.bandTarget[k], baseline)
      // Pulse eases toward the target (ease-in).
      this.bandPulse[k] += (this.bandTarget[k] - this.bandPulse[k]) * kA
    }
  }

  /** Zone reaction config (shapes that ride the surface). */
  setZoneReaction(cfg: ZoneReactionConfig) {
    this.zoneConfig = cfg
  }

  /** Per-band images (index 0..3) for zone shapes whose type is 'image'. */
  setZoneImages(urls: (string | null)[]) {
    this.zones.setImages(urls)
  }

  /**
   * Beat-synced camera motions. The phase advances at the track's tempo (BPM),
   * so the movement breathes with the music; energy widens the amplitude. The
   * musical beat is computed beforehand (updateMusicalBeat) and read via beatTotal.
   */
  private updateBeatCamera(mode: string, frame: AudioFrame, dt: number) {
    const cam = this.camera
    const R = 7
    const energy = 0.6 + frame.level * 0.6

    // Step-based motion: the pose changes every N beats (move, then hold).
    const n = Math.max(1, Math.round(this.params.cameraBeats ?? 4))
    const step = Math.floor(this.beatTotal / n)

    // Retarget on a new step OR when the mode changes; then animate to the pose.
    if (step !== this.camStep || mode !== this.camMode) {
      this.camStep = step
      this.camMode = mode
      this.camFrom.copy(cam.position)
      this.setBeatTarget(mode, step, energy, R)
      this.camU = 0
    }

    const speed = this.params.cameraSpeed ?? 1
    const duration = 0.6 / Math.max(0.1, speed)
    this.camU = Math.min(1, this.camU + dt / duration)
    const e = ease01(this.camU, this.params.cameraEaseIn ?? 0.4, this.params.cameraEaseOut ?? 0.4)
    cam.position.lerpVectors(this.camFrom, this.camTo, e)
    cam.up.set(0, 1, 0)
    cam.lookAt(0, 0, 0)
  }

  /** Computes the target pose for a beat-motion step into this.camTo. */
  private setBeatTarget(mode: string, step: number, energy: number, R: number) {
    const dir = step % 2 === 0 ? 1 : -1
    if (mode === 'backforth') {
      this.camTo.set(0, 0, R + dir * 3 * energy)
    } else if (mode === 'leftright') {
      const az = dir * 0.7 * energy
      this.camTo.set(Math.sin(az) * R, 0, Math.cos(az) * R)
    } else if (mode === 'updown') {
      const el = dir * 0.6 * energy
      this.camTo.set(0, Math.sin(el) * R, Math.cos(el) * R)
    } else if (mode === 'spiral') {
      const az = step * (Math.PI / 2)
      const el = dir * 0.4 * energy
      const r = R + dir * 1.0
      this.camTo.set(Math.cos(el) * Math.sin(az) * r, Math.sin(el) * r, Math.cos(el) * Math.cos(az) * r)
    } else {
      this.camTo.set(0, 0, R)
    }
  }

  /**
   * Recenters the camera onto the active primitive's real mass.
   *
   * We don't assume the shape sits at the origin (some primitives are built
   * offset or with very different sizes): we measure its world bounding box,
   * aim at its center and pull back just enough to frame it. The current
   * viewing angle is preserved (we only re-center and re-fit distance).
   */
  recenterCamera() {
    // Measure without the beat "pulse" (group.scale) so the framing is stable.
    const prevScale = this.group.scale.clone()
    this.group.scale.setScalar(1)
    const box = new THREE.Box3().setFromObject(this.group)
    this.group.scale.copy(prevScale)

    const isStatic = (this.params.cameraMode ?? 'static') === 'static'

    const center = new THREE.Vector3(0, 0, 0)
    let radius = 4
    if (!box.isEmpty()) {
      box.getCenter(center)
      const sphere = box.getBoundingSphere(new THREE.Sphere())
      if (sphere.radius > 0) radius = sphere.radius
    }
    // Static aims at the ORIGIN (where primitives are built), not the measured
    // box center — stray/asymmetric geometry (streaks, kaleido) would otherwise
    // skew the center and push the shape off-screen.
    if (isStatic) center.set(0, 0, 0)

    // Static frames tight (close); other modes leave 20% of air.
    const fov = THREE.MathUtils.degToRad(this.camera.fov)
    const dist = (radius / Math.sin(fov / 2)) * (isStatic ? 0.55 : 1.2)

    // Static = clean frontal, dead-centered view. Other modes keep the current
    // viewing direction (so the ⊕ button re-centers without losing your angle).
    const dir = new THREE.Vector3()
    if (isStatic) {
      dir.set(0, 0, 1)
    } else {
      dir.subVectors(this.camera.position, this.controls.target)
      if (dir.lengthSq() < 1e-6) dir.set(0, 0.15, 1)
      dir.normalize()
    }

    this.controls.target.copy(center)
    this.camera.up.set(0, 1, 0)
    this.camera.position.copy(center).addScaledVector(dir, dist)
    this.camera.lookAt(center)
    this.controls.resetMotion()
    // In cinematic mode, sync the director so it starts from here.
    this.director.syncFrom(this.camera)
  }

  /**
   * Manual camera placement that ALWAYS fills the frame (unlike a CSS scale,
   * which boxes a shrunk render). `zoom` scales the perspective FOV (>1 zooms
   * in, <1 zooms out — showing more, still full-bleed); `xFrac`/`yFrac` pan the
   * frustum (fraction of the viewport, +x = right, +y = up). Re-applied each
   * frame by the host so the director / scene swaps can't reset it.
   */
  setManualCamera(xFrac: number, yFrac: number, zoom: number): void {
    const cam = this.camera
    const w = Math.max(1, this.container.clientWidth)
    const h = Math.max(1, this.container.clientHeight)
    cam.zoom = zoom > 0.05 ? zoom : 1
    if (Math.abs(xFrac) < 1e-4 && Math.abs(yFrac) < 1e-4) {
      cam.clearViewOffset()
    } else {
      cam.setViewOffset(w, h, -xFrac * w, yFrac * h, w, h)
    }
    cam.updateProjectionMatrix()
  }

  /** Current camera pose (position + look target + up), for saving in a scene. */
  getCameraPose(): CameraPose {
    return {
      position: this.camera.position.toArray() as [number, number, number],
      target: this.controls.target.toArray() as [number, number, number],
      up: this.camera.up.toArray() as [number, number, number],
    }
  }

  /** Restores a saved camera pose (meaningful for static/auto-rotate; beat modes
   *  re-drive the camera each frame). */
  applyCameraPose(p: CameraPose) {
    this.camera.position.fromArray(p.position)
    this.camera.up.fromArray(p.up)
    this.controls.target.fromArray(p.target)
    this.camera.lookAt(this.controls.target)
    this.controls.resetMotion()
    this.director.syncFrom(this.camera)
    // Mark the camera as already settled in manual mode, so the per-frame loop's
    // "leaving procedural / inside" transition resets don't stomp this restored
    // pose on the next frame (this is what dropped the saved static position).
    this.wasProcedural = false
    this.wasInside = false
  }

  /** Sizes this layer to an explicit viewport (the offscreen layer shares the
   *  renderer, so it must NOT resize it — the primary owns the drawing buffer). */
  private resizeTo(w: number, h: number) {
    // updateStyle=true (default): set the canvas CSS size to w×h so it isn't
    // displayed at the drawing-buffer size (w×h×pixelRatio), which on HiDPI/zoom
    // made the canvas 2× too big and pushed the content off to one side.
    if (this.ownsRenderer) this.renderer.setSize(w, h)
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
    // EffectComposer applies the pixelRatio internally -> pass the size in CSS px.
    this.composer?.setSize(w, h)
    this.bloomPass?.setSize(w, h)
    this.uniforms.uPixelRatio.value = this.renderer.getPixelRatio()
    // Keep the fullscreen "contain" math in sync with the new viewport aspect.
    this.uniforms.uScreenAspect.value = this.camera.aspect
    // Drawing-buffer resolution for screen-space primitive texturing.
    const pr = this.renderer.getPixelRatio()
    this.uniforms.uResolution.value.set(w * pr, h * pr)
  }

  private resize() {
    const w = this.container.clientWidth || 1
    const h = this.container.clientHeight || 1
    this.resizeTo(w, h)
    this.compositor?.setAspect(w / h)
    this.layerB?.resizeTo(w, h)
  }

  start() {
    this.controls.autoRotate = this.params.cameraMode === 'autorotate'
    this.controls.autoRotateSpeed = this.params.rotationSpeed * 0.8
    const loop = () => {
      this.rafId = requestAnimationFrame(loop)
      const dt = Math.min(this.clock.getDelta(), 0.1)
      const time = this.clock.elapsedTime
      const frame = this.readAudio(dt)
      if (this.tx && this.layerB && this.compositor) {
        // Transitioning: render BOTH layers off-screen, then composite to screen.
        const progress = Math.min(1, (performance.now() - this.tx.start) / this.tx.durMs)
        this.renderFrame(frame, time, dt, false) // incoming (this scene)
        this.layerB.renderFrame(frame, time, dt, false) // outgoing clone
        this.compositor.render(
          this.renderer,
          this.layerB.getOutputTexture(),
          this.getOutputTexture(),
          this.tx.cfg,
          progress,
          time,
        )
        if (progress >= 1) this.endTransition()
      } else {
        this.renderFrame(frame, time, dt, true) // straight to screen (idle)
      }
    }
    loop()
  }

  /** Renders ONE frame of this layer (audio → primitive → camera → post).
   *  `toScreen` false renders into the composer's buffer so getOutputTexture()
   *  can be sampled by the compositor during a transition. */
  private renderFrame(frame: AudioFrame, time: number, dt: number, toScreen: boolean) {
    updateUniforms(this.uniforms, this.freqTex, this.waveTex, frame, this.params, time)
      // Energy-scaled clock: advances faster on loud/beat parts (no phase jumps),
      // so shapes can genuinely speed up with the music.
      this.energyTime += dt * (0.4 + frame.level * 1.8 + frame.beatIntensity * 1.4)
      this.uniforms.uEnergyTime.value = this.energyTime
      this.primitive?.update(this.params, time, dt)
      // Clean musical beat for the frame, shared by the camera and the video loop.
      this.updateMusicalBeat(frame, dt)
      // Reliable on-beat pulse from the BPM grid (the audio beat detector saturates
      // on sustained music; this never stops). Shapes use it for steady reactions.
      this.beatPulse = Math.max(0, this.beatPulse - dt * 3)
      if (this.frameMusicalBeat) this.beatPulse = 1
      this.uniforms.uBeatPulse.value = this.beatPulse
      // Ease the reactive signals (bands, beat, spectrum) so the primitive push
      // has attack/release smoothing instead of snapping. Must run AFTER
      // updateUniforms (which writes the raw values this overwrites).
      this.applyReactiveEasing(frame, dt)
      // Whole-primitive scale pulse — uses the EASED beat so the zoom punch is
      // smooth instead of an abrupt snap on every beat.
      const pulse = 1 + this.beatEnv * 0.05 * this.params.reactivity
      this.group.scale.setScalar(pulse)
      // Continuous primitive spin (Rotation slider), independent of the camera
      // mode so it works even when the camera is Static.
      this.group.rotation.y += this.params.rotationSpeed * dt
      // Per-band onset pulses (bass/mid/treble/level) via spectral flux — used by
      // Zone reaction to deform the mesh under each band's moving shapes.
      this.updateBandPulses(frame, dt)

      const zoneOn = this.zoneConfig.enabled
      if (zoneOn) {
        // Zone reaction localizes the deformation to the moving shapes; the
        // whole-primitive random lobe is suppressed. Reactivity scales every
        // band's push; Bass punch additionally scales the BASS band (index 0),
        // so both sliders work in zone mode and bass punch is tied to the bass.
        const reactivity = this.params.reactivity
        const bassPunch = this.params.bassPunch
        this.zoneBandScaled[0] = this.bandPulse[0] * reactivity * bassPunch
        this.zoneBandScaled[1] = this.bandPulse[1] * reactivity
        this.zoneBandScaled[2] = this.bandPulse[2] * reactivity
        this.zoneBandScaled[3] = this.bandPulse[3] * reactivity
        this.zones.update(this.zoneConfig, this.zoneBandScaled, dt, this.beatPeriod)
        this.seedPulse = 0
        this.uniforms.uSeedPulse.value = 0
      } else {
        this.zones.disable()
        // Random deformation, ONLY when enabled (seedAuto). When off, the seed lobe
        // is fully disabled (uSeedPulse = 0) so the primitive looks like the original.
        // When on, each percussive HIT (snare/boom) punches the deformation and
        // picks a fresh random shape.
        this.hitCooldown = Math.max(0, this.hitCooldown - dt)
        const high = frame.mid * 0.7 + frame.treble * 0.5
        const bassFlux = frame.bass - this.prevBass
        const highFlux = high - this.prevHigh
        this.prevBass = frame.bass
        this.prevHigh = high
        if (this.params.seedAuto) {
          this.seedPulse = Math.max(0, this.seedPulse - dt * 3)
          if (!this.wasSeedAuto) this.autoSeed = Math.random() * 100 // seed on enable
          const boom = bassFlux > 0.05 && frame.bass > 0.2
          const snare = highFlux > 0.04 && high > 0.15
          if ((boom || snare) && this.hitCooldown <= 0) {
            this.hitCooldown = 0.11 // debounce a single hit (~max 9 hits/s)
            this.seedPulse = Math.min(1, 0.55 + Math.max(bassFlux, highFlux) * 3.0)
            this.autoSeed = Math.random() * 100 // fresh random shape on each hit
          }
          const s = this.autoSeed
          this.uniforms.uSeed.value.set(s, s * 1.37, s * 0.71)
          this.uniforms.uSeedPulse.value = this.seedPulse
        } else {
          this.seedPulse = 0
          this.uniforms.uSeedPulse.value = 0
        }
        this.wasSeedAuto = this.params.seedAuto
      }
      // Camera: wrap-around LED shapes (sphere/cylinder) put the camera inside
      // and look around; otherwise 'cinematic'/beat motions drive it procedurally
      // (controls off) and 'static'/'autorotate' use CameraControls.
      const mode = this.params.cameraMode ?? 'static'
      const inside =
        this.params.primitive === 'ledpanel' &&
        (this.params.ledShape === 'sphere' || this.params.ledShape === 'cylinder')
      if (inside) {
        this.controls.enabled = false
        this.insideLook.enabled = true
        // Cinematic camera mode = gentle auto-pan; any other mode = drag to look.
        this.insideLook.update(this.camera, frame, dt, mode === 'cinematic')
        this.wasProcedural = false
      } else {
        this.insideLook.enabled = false
        if (this.wasInside) {
          // Leaving a wrap shape: place the camera back outside.
          this.camera.position.set(0, 0, 7)
          this.camera.up.set(0, 1, 0)
          this.camera.lookAt(0, 0, 0)
          this.controls.target.set(0, 0, 0)
          this.controls.resetMotion()
          this.controls.enabled = true
        }
        // Mode families:
        //  • 'static'  → locked centered view (orbit disabled, pose held).
        //  • 'free' / 'autorotate' → user can orbit/drag via CameraControls.
        //  • everything else → driven procedurally (controls disabled).
        const procedural = mode !== 'static' && mode !== 'autorotate' && mode !== 'free'
        if (procedural) {
          if (!this.wasProcedural) {
            this.director.syncFrom(this.camera)
            this.controls.enabled = false
          }
          if (mode === 'cinematic') {
            this.director.update(this.camera, frame, dt)
          } else {
            this.updateBeatCamera(mode, frame, dt)
          }
        } else if (mode === 'static') {
          // Locked: no orbit, no drift. The centered pose was set on entering
          // static (applyParams → recenterCamera) and held by leaving it fixed.
          this.controls.enabled = false
          this.controls.autoRotate = false
        } else {
          // free / autorotate — hand the camera to the user.
          if (this.wasProcedural) {
            // Back to manual without roll inherited from the procedural driver.
            this.camera.up.set(0, 1, 0)
            this.camera.lookAt(0, 0, 0)
            this.controls.target.set(0, 0, 0)
            this.controls.resetMotion()
          }
          this.controls.enabled = true
          this.controls.autoRotate = mode === 'autorotate'
          this.controls.update(dt)
        }
        this.wasProcedural = procedural
      }
      this.wasInside = inside

      // Trails (afterimage) reactive to the music.
      const trails = this.params.trails ?? 0
      this.afterimagePass.uniforms['damp'].value = trails > 0.01 ? 0.6 + trails * 0.38 : 0
      // Reactive bloom: base glow + boost with level/beat.
      const bloom = this.params.bloom ?? 0
      this.bloomPass.strength = bloom * (1 + frame.level * 0.8 + frame.beatIntensity * 0.6)

      // Background video playback style (linear / acceleration / ping-pong).
      this.updateVideoLoop(dt, frame.level)

      // Video Lab: recolors the gray video into its render target (it's the background).
      this.videoLab?.render(this.renderer)

      // Tunnel background follows the camera (orbit/dolly/look).
      if (this.uniforms.uTunnel.value > 0.5) this.updateTunnelCamera()

      // Refresh the fullscreen cover aspect (video metadata may load late).
      if (this.uniforms.uMediaFull.value > 0.5) this.updateScreenMediaAspect()
      // Refresh the primitive texture aspect (independent media).
      if (this.uniforms.uTexturePrimitive.value > 0.5 && this.texTexture) {
        const img = this.texTexture.image as
          | { videoWidth?: number; videoHeight?: number; width?: number; height?: number }
          | undefined
        const tw = img?.videoWidth || img?.width || 0
        const th = img?.videoHeight || img?.height || 0
        if (tw > 0 && th > 0) this.uniforms.uPrimitiveAspect.value = tw / th
      }

      this.composer.renderToScreen = toScreen
      this.composer.render()
  }

  /** Final composited image of the last renderFrame (valid when toScreen=false). */
  getOutputTexture(): THREE.Texture {
    return this.composer.readBuffer.texture
  }

  /** The live WebGL canvas (preserveDrawingBuffer is on, so it can be sampled at
   *  any time — used by the thumbnail capture and the video exporter). */
  getCanvas(): HTMLCanvasElement {
    return this.renderer.domElement
  }

  /** Returns a JPEG data URL of the current look — the thumbnail for a saved
   *  scene / timeline clip. Copies the LIVE on-screen canvas (the exact
   *  composited, bloomed, tone-mapped, sRGB image the user sees), center-cropped
   *  to the thumbnail aspect — so the thumbnail matches the visual precisely
   *  instead of looking darker. Relies on the renderer's preserveDrawingBuffer. */
  captureThumbnail(w = 160, h = 90): string {
    const src = this.renderer.domElement
    const sw = src.width
    const sh = src.height
    if (!sw || !sh) return ''
    const cv = document.createElement('canvas')
    cv.width = w
    cv.height = h
    const ctx = cv.getContext('2d')
    if (!ctx) return ''
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    // object-fit: cover — crop the live canvas to the thumb's aspect (no stretch).
    const scale = Math.max(w / sw, h / sh)
    const cw = w / scale
    const ch = h / scale
    ctx.drawImage(src, (sw - cw) / 2, (sh - ch) / 2, cw, ch, 0, 0, w, h)
    return cv.toDataURL('image/jpeg', 0.85)
  }

  /** Starts a transition: clones the OUTGOING look into an offscreen layer that
   *  keeps animating, then composites it over this (incoming) scene for durMs.
   *  Call BEFORE the store applies the new scene, so the clone captures the OLD
   *  look while this scene becomes the new one via the normal store flow. */
  beginTransition(oldDesc: SceneDescription, cfg: TransitionConfig) {
    if (this.offscreen) return
    if (this.layerB) {
      this.layerB.dispose()
      this.layerB = null
    }
    if (!this.compositor) this.compositor = new TransitionCompositor()
    const b = new Scene(this.container, oldDesc.params, this.readAudio, {
      renderer: this.renderer,
      offscreen: true,
    })
    b.applySceneDescription(oldDesc)
    // Seed B's animation phase from the live look so it doesn't visibly pop at t=0.
    b.energyTime = this.energyTime
    b.beatTotal = this.beatTotal
    b.group.rotation.copy(this.group.rotation)
    const w = this.container.clientWidth || 1
    const h = this.container.clientHeight || 1
    b.resizeTo(w, h)
    this.compositor.setAspect(w / h)
    this.compositor.setSeed(Math.random())
    this.layerB = b
    this.tx = { cfg, start: performance.now(), durMs: Math.max(80, cfg.durationMs) }
  }

  private endTransition() {
    this.tx = null
    if (this.layerB) {
      this.layerB.dispose()
      this.layerB = null
    }
  }

  /** Configures this layer to fully render a given scene description (used to
   *  rebuild the outgoing look on the clone, mirroring VisualCanvas's mapping). */
  applySceneDescription(d: SceneDescription) {
    this.setParams(d.params)
    this.setColorBackground(
      d.params.bgColorMode,
      d.params.bgGradientType,
      d.params.bgGradientAngle,
      d.params.bgColorStops,
    )
    const vl = d.videoLab
    const appearance = {
      palette: vl.palette,
      bands: vl.bands,
      smooth: vl.smooth,
      intensity: vl.intensity,
      black: vl.black,
      white: vl.white,
      smoothing: vl.smoothing,
      blend: vl.blend,
    }
    const mediaUrl =
      d.params.backgroundType === 'image'
        ? d.backgroundImageUrl
        : d.params.backgroundType === 'video'
          ? d.backgroundVideoUrl
          : null
    const recolor = vl.active && d.params.backgroundType === 'video' && !!mediaUrl
    if (recolor) {
      this.setBackgroundVideo(null)
      this.setVideoLabSource(mediaUrl, appearance)
    } else {
      this.setVideoLabSource(null, appearance)
      this.setBackgroundVideo(mediaUrl)
    }
    const pl = d.primitiveLab
    this.setPrimitiveTexture(d.textureUrl)
    this.setPrimitiveRecolor(pl.active, {
      palette: pl.palette,
      bands: pl.bands,
      smooth: pl.smooth,
      intensity: pl.intensity,
      black: pl.black,
      white: pl.white,
      smoothing: pl.smoothing,
      blend: pl.blend,
    })
    this.setZoneReaction(d.zoneReaction)
    this.setZoneImages(d.zoneReaction.perBand.map((b) => b.imageUrl ?? null))
    if (d.camera) this.applyCameraPose(d.camera)
  }

  stop() {
    cancelAnimationFrame(this.rafId)
    this.rafId = 0
  }

  dispose() {
    this.stop()
    if (this.layerB) {
      this.layerB.dispose()
      this.layerB = null
    }
    this.compositor?.dispose()
    this.compositor = null
    this.tx = null
    this.resizeObserver?.disconnect()
    this.sphereBg.geometry.dispose()
    ;(this.sphereBg.material as THREE.Material).dispose()
    this.bgColorMesh.geometry.dispose()
    ;(this.bgColorMesh.material as THREE.Material).dispose()
    this.bgCanvasTex.dispose()
    this.setBackgroundVideo(null)
    this.setPrimitiveTexture(null)
    this.primLut?.dispose()
    this.videoLab?.dispose()
    this.videoLab = null
    this.primitive?.dispose()
    this.controls.dispose()
    this.insideLook.dispose()
    this.freqTex.dispose()
    this.waveTex.dispose()
    this.dummyMedia.dispose()
    this.composer.dispose()
    // The offscreen layer borrows the primary's renderer — never dispose it here.
    if (this.ownsRenderer) {
      this.renderer.dispose()
      if (this.renderer.domElement.parentElement === this.container) {
        this.container.removeChild(this.renderer.domElement)
      }
    }
  }
}
